/**
 * Helpers for the site Promo codes section (castr `/sites/:id/promos*`,
 * internal/sites-control-plane-api.md §6f). A code is one group of paywall
 * promos — one row per plan it covers — so limits count per plan.
 */
import { formatMoney } from '@/Utils/siteTransactions'

export const PROMO_CODE_RE = /^[A-Z0-9_-]{3,32}$/
export const PROMO_TYPES = ['percent', 'fixed', 'comp']
export const PROMO_TYPE_LABELS = { percent: 'Percentage off', fixed: 'Amount off', comp: 'Free (100 % off)' }

/** What castr does to the code before validating: trim + uppercase. */
export function normalizeCode(input) {
	return String(input || '').toUpperCase().replace(/\s+/g, '')
}

/** Inline message for the code field, or '' when it passes castr's rule. */
export function codeError(code) {
	const c = normalizeCode(code)
	if (!c) return ''
	if (c.length < 3) return 'At least 3 characters.'
	if (c.length > 32) return 'At most 32 characters.'
	if (!PROMO_CODE_RE.test(c)) return 'Only letters, digits, “-” and “_”.'
	return ''
}

/** Inline message for the value field given the type, or ''. */
export function valueError(type, value) {
	if (type === 'comp') return ''
	const s = String(value ?? '').trim()
	if (!s) return type === 'percent' ? 'Enter a percentage between 1 and 100.' : 'Enter an amount greater than 0.'
	const n = Number(s)
	if (!Number.isFinite(n) || n <= 0) return 'Enter a number greater than 0.'
	if (type === 'percent' && n > 100) return 'A percentage cannot exceed 100.'
	if (type === 'fixed' && n > 1_000_000) return 'Amount too large.'
	return ''
}

/** "20 % off" / "$5.00 off" / "Free". */
export function promoValueLabel(promo) {
	if (!promo) return ''
	if (promo.type === 'comp') return 'Free'
	if (promo.type === 'percent') return `${Number(promo.value)} % off`
	return `${formatMoney(promo.value, promo.currency || plansCurrency(promo.products))} off`
}

/** Currency shared by the given plans (or `products[]` of a promo), else ''. */
export function plansCurrency(plans) {
	const set = new Set((plans || []).map((p) => String(p.currency || '').toUpperCase()).filter(Boolean))
	return set.size === 1 ? [...set][0] : ''
}

/**
 * @returns {{ state: 'active'|'inactive'|'expired'|'exhausted', label: string, variant: string }}
 */
export function promoStatus(promo, now = Date.now()) {
	if (!promo?.active) return { state: 'inactive', label: 'Inactive', variant: 'muted' }
	if (promo.expires_at && new Date(promo.expires_at).getTime() <= now) return { state: 'expired', label: 'Expired', variant: 'warning' }
	const max = Number(promo.max_redemptions)
	const rows = (promo.products || []).filter((p) => !p.missing)
	if (max > 0 && rows.length && rows.every((p) => Number(p.redemptions) >= max)) return { state: 'exhausted', label: 'Limit reached', variant: 'warning' }
	return { state: 'active', label: 'Active', variant: 'success' }
}

/** "3 / 100 per plan" or "3 · unlimited". */
export function redemptionsLabel(promo) {
	const n = Number(promo?.redemptions) || 0
	const max = Number(promo?.max_redemptions)
	if (max > 0) return `${n} / ${max} per plan`
	return `${n} · unlimited`
}

/** "All plans (3)" or the plan titles (first two + count). */
export function promoPlansLabel(promo) {
	const products = promo?.products || []
	if (promo?.scope === 'site') return `All plans at creation (${products.length})`
	if (!products.length) return 'No plans'
	const names = products.slice(0, 2).map((p) => p.title || p.public_id)
	return names.join(', ') + (products.length > 2 ? ` +${products.length - 2}` : '')
}

/** Why a code cannot be created as asked — castr `reason` → owner-facing text. */
export const PROMO_REASON_TEXT = {
	code_taken: 'This code already exists on this site. Pick another code, or delete the existing one first.',
	not_allowed_on_subscriptions: 'Percentage and amount discounts cannot be used on subscription plans — switch the type to Free (100 % off) or pick one-time plans only.',
	invalid_code: 'Codes are 3–32 characters: letters, digits, “-” and “_”.',
	invalid_value: 'Check the discount value.',
	invalid_max_redemptions: 'The redemption limit must be a whole number of at least 1.',
	invalid_expires_at: 'The expiry must be in the future.',
	no_products: 'This site has no active plan to attach the code to — create a plan first.',
	product_archived: 'One of the selected plans is archived. Pick active plans only.',
	unknown_product: 'One of the selected plans no longer exists. Reload and try again.',
	paywall_site_unconfigured: 'This site is not linked to your Paywall account yet. Retry publish from the Overview tab.',
}
